import { Component } from '@theme/component';

/**
 * Comparison slider component for comparing two images
 *
 * @typedef {object} ComparisonSliderRefs
 * @property {HTMLElement} [slidesContainer] - The container for slides
 * @property {HTMLElement[]} [mediaWrappers] - All slide media wrappers
 * @property {HTMLInputElement[]} [sliders] - All slide range inputs
 *
 * @extends {Component<ComparisonSliderRefs>}
 *
 * @property {string[]} requiredRefs - No required refs (supports 1+ slides)
 */
export class ComparisonSliderComponent extends Component {
  requiredRefs = [];
  orientation = 'horizontal';
  rafId = 0;
  #isDragging = false;
  #pointerId = null;
  #currentSlideIndex = 0;
  /** @type {HTMLElement | null} */
  #activeMediaWrapper = null;
  /** @type {HTMLInputElement | null} */
  #activeSlider = null;

  constructor() {
    super();
  }

  /** @returns {HTMLElement[]} */
  #getSlides() {
    return Array.from(this.querySelectorAll('[data-comparison-slide]'));
  }

  /** @returns {HTMLElement | null} */
  #getSlideByIndex(index) {
    const slides = this.#getSlides();
    return slides[index] ?? null;
  }

  /** @returns {number} */
  #getActiveSlideIndex() {
    const slides = this.#getSlides();
    const activeIndex = slides.findIndex((slide) => !slide.hasAttribute('hidden'));
    return activeIndex >= 0 ? activeIndex : 0;
  }

  #updateActiveRefsFromDOM() {
    const slides = this.#getSlides();
    if (!slides.length) return;

    this.#currentSlideIndex = this.#getActiveSlideIndex();
    const activeSlide = slides[this.#currentSlideIndex];
    if (!activeSlide) return;

    const mediaWrapper = activeSlide.querySelector('[data-comparison-slider]');
    const slider = activeSlide.querySelector('input[type="range"]');

    this.#activeMediaWrapper = mediaWrapper instanceof HTMLElement ? mediaWrapper : null;
    this.#activeSlider = slider instanceof HTMLInputElement ? slider : null;

    const orientation = this.#activeMediaWrapper?.dataset.orientation;
    this.orientation = orientation === 'vertical' ? 'vertical' : 'horizontal';
  }

  #attachPointerListeners() {
    const mediaWrapper = this.#activeMediaWrapper;
    if (!mediaWrapper) return;

    mediaWrapper.addEventListener('pointerdown', this.#onPointerDown);
    mediaWrapper.addEventListener('pointermove', this.#onPointerMove);
    mediaWrapper.addEventListener('pointerup', this.#onPointerUp);
    mediaWrapper.addEventListener('pointercancel', this.#onPointerUp);
    mediaWrapper.addEventListener('lostpointercapture', this.#onPointerUp);
  }

  #detachPointerListeners() {
    const mediaWrapper = this.#activeMediaWrapper;
    if (!mediaWrapper) return;

    mediaWrapper.removeEventListener('pointerdown', this.#onPointerDown);
    mediaWrapper.removeEventListener('pointermove', this.#onPointerMove);
    mediaWrapper.removeEventListener('pointerup', this.#onPointerUp);
    mediaWrapper.removeEventListener('pointercancel', this.#onPointerUp);
    mediaWrapper.removeEventListener('lostpointercapture', this.#onPointerUp);
  }

  /**
   * Called when component is added to DOM
   */
  connectedCallback() {
    super.connectedCallback();
    this.#updateActiveRefsFromDOM();
    this.sync();
    this.#attachPointerListeners();
  }

  disconnectedCallback() {
    this.#detachPointerListeners();

    if (this.rafId) cancelAnimationFrame(this.rafId);
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#detachPointerListeners();
    this.#updateActiveRefsFromDOM();
    this.#attachPointerListeners();
    this.sync();
  }

  /**
   * Handle pointer move event
   * @param {PointerEvent} event
   */
  handlePointerMove(event) {
    this.#updateFromPointerEvent(event);
  }

  #onPointerDown = (event) => {
    if (!(event instanceof PointerEvent)) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    const mediaWrapper = this.#activeMediaWrapper;
    if (!mediaWrapper) return;

    this.#isDragging = true;
    this.#pointerId = event.pointerId;

    if (event.cancelable) event.preventDefault();

    try {
      mediaWrapper.setPointerCapture(event.pointerId);
    } catch {}

    this.#updateFromPointerEvent(event);
  };

  #onPointerMove = (event) => {
    if (!(event instanceof PointerEvent)) return;
    if (!this.#isDragging) {
      if (event.pointerType === 'mouse') this.#updateFromPointerEvent(event);
      return;
    }

    if (this.#pointerId != null && event.pointerId !== this.#pointerId) return;
    if (event.cancelable) event.preventDefault();
    this.#updateFromPointerEvent(event);
  };

  #onPointerUp = (event) => {
    if (!(event instanceof PointerEvent)) return;
    if (this.#pointerId != null && event.pointerId !== this.#pointerId) return;
    this.#isDragging = false;
    this.#pointerId = null;
  };

  #updateFromPointerEvent(event) {
    const mediaWrapper = this.#activeMediaWrapper;
    const slider = this.#activeSlider;
    if (!mediaWrapper || !slider) return;

    const rect = mediaWrapper.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    let value = 50;
    if (this.orientation === 'horizontal') {
      const x = event.clientX - rect.left;
      value = (x / rect.width) * 100;
    } else {
      const y = event.clientY - rect.top;
      value = 100 - (y / rect.height) * 100;
    }

    value = Math.max(0, Math.min(100, value));

    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(() => {
      slider.value = String(value);
      this.sync();
    });
  }

  /**
   * Sync the CSS custom property with the input value
   */
  sync(event) {
    const target = event?.target;
    const slider = target instanceof HTMLInputElement ? target : this.#activeSlider;
    const mediaWrapper =
      slider instanceof HTMLElement ? slider.closest('[data-comparison-slider]') : this.#activeMediaWrapper;

    if (!(mediaWrapper instanceof HTMLElement) || !(slider instanceof HTMLInputElement)) return;

    const val = (Number(slider.value) - Number(slider.min)) / (Number(slider.max) - Number(slider.min) || 1);
    const compareValue = Math.round(val * 100);

    // Set the CSS custom property on the media wrapper
    mediaWrapper.style.setProperty('--compare', String(compareValue));
  }



  /**
   * Set the slider value and update display
   * @param {number} value - Value between 0-100 (0 = all after, 100 = all before)
   */
  setValue(value) {
    const slider = this.#activeSlider;
    if (!slider) return;

    slider.value = String(value);
    this.sync();
  }

  previous() {
    this.#selectSlide(this.#currentSlideIndex - 1);
  }

  next() {
    this.#selectSlide(this.#currentSlideIndex + 1);
  }

  #selectSlide(index) {
    const slides = this.#getSlides();
    if (slides.length <= 1) return;

    const nextIndex = ((index % slides.length) + slides.length) % slides.length;
    if (nextIndex === this.#currentSlideIndex) return;

    slides.forEach((slide, i) => {
      if (i === nextIndex) slide.removeAttribute('hidden');
      else slide.setAttribute('hidden', '');
    });

    this.#detachPointerListeners();
    this.#currentSlideIndex = nextIndex;
    this.#updateActiveRefsFromDOM();
    this.sync();
    this.#attachPointerListeners();
  }

}

// Register the custom element
if (!customElements.get('comparison-slider-component')) {
  customElements.define('comparison-slider-component', ComparisonSliderComponent);
}

const syncAllComparisonSliders = () => {
  document.querySelectorAll('comparison-slider-component').forEach((el) => {
    if (typeof el.sync === 'function') el.sync();
  });
};

document.addEventListener('shopify:section:load', syncAllComparisonSliders);
document.addEventListener('shopify:section:reorder', syncAllComparisonSliders);
